import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';
import { Camera, Maximize2, X } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { MAX_LIVE, LIVE_FEEDS, buildTiles } from '../data/cctvFeeds';
import CctvAnotasi from './CctvAnotasi';

// Dinding Monitor CCTV: grid 2x2 / 3x3 / 4x4.
// Sel pertama memutar siaran live HLS Jakarta; sel sisanya rekaman berulang
// supaya monitor tidak pernah kosong. Semua sel tampil seperti kamera aktif.
// Status asli (live / rekaman / gagal) hanya disimpan di data internal, tidak
// ditampilkan sebagai OFFLINE (permintaan Andra 28 Okt 2026).
//
// Tanpa efek grain atau scanline: video tampil natural apa adanya. Label kamera,
// jam WIB, dan titik REC tetap ada.

const SESSION_KEY_COLS = 'horus_cctv_wall_cols';
const OPTIONS = [2, 3, 4];

function loadCols() {
  try {
    const saved = parseInt(sessionStorage.getItem(SESSION_KEY_COLS), 10);
    if (OPTIONS.includes(saved)) return saved;
  } catch { /* sessionStorage bisa diblokir */ }
  return 3; // bawaan saat dibuka: 3x3
}

const WIB = 'Asia/Jakarta';

function formatClock(d) {
  const jam = d.toLocaleTimeString('id-ID', { timeZone: WIB, hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const tanggal = d.toLocaleDateString('id-ID', { timeZone: WIB, day: '2-digit', month: '2-digit', year: 'numeric' });
  return { jam, tanggal };
}

const OK = { label: 'ONLINE', color: '#00ff88' };

// Satu sel. `cam.live` true hanya untuk sel yang boleh menarik siaran langsung;
// sel lain memutar rekaman berulang. Bila siaran live gagal, sel ikut memutar
// rekaman tetapi tetap tampil ONLINE (tanpa label OFFLINE).
export const CctvCell = ({ cam, index }) => {
  const { lang } = useApp();
  const videoRef = useRef(null);
  const boxRef = useRef(null);
  const hlsRef = useRef(null);
  const [visible, setVisible] = useState(true);
  const [link, setLink] = useState(cam.live ? 'linking' : 'klip');

  // Sel yang keluar layar dijeda dan unduhan segmennya dihentikan.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin: '60px', threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    let cancelled = false;
    let watchdog = null;

    // Rekaman berulang: sel cadangan, atau siaran live yang sudah gagal.
    if (!cam.live || !cam.url || link === 'fallback' || link === 'klip') {
      v.loop = true;
      v.src = cam.clip;
      v.play().catch(() => { /* autoplay bisa ditolak; sel tetap tampil */ });
      return () => { v.removeAttribute('src'); v.load(); };
    }

    const fallback = () => { if (!cancelled) setLink('fallback'); };

    if (!Hls.isSupported()) {
      if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = cam.url;
        v.loop = false;
        v.play().catch(() => {});
        v.addEventListener('playing', () => { clearTimeout(watchdog); setLink('ok'); });
        watchdog = setTimeout(fallback, 10000);
        return () => { cancelled = true; clearTimeout(watchdog); };
      }
      fallback();
      return () => { cancelled = true; };
    }

    let retry = 0;
    const hls = new Hls({ maxBufferLength: 4, capLevelToPlayerSize: true, enableWorker: true });
    hlsRef.current = hls;

    const onPlaying = () => { clearTimeout(watchdog); setLink('ok'); };
    v.addEventListener('playing', onPlaying);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      v.play().catch(() => {});
      clearTimeout(watchdog);
      // Manifest sempat terbaca, tapi decoder belum jalan. Jangan tunggu selamanya.
      watchdog = setTimeout(fallback, 12000);
    });

    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data.fatal) return;
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && retry < 1) {
        retry += 1;
        try { hls.recoverMediaError(); return; } catch { /* lanjut ke fallback */ }
      }
      if (retry < 2) {
        retry += 1;
        setTimeout(() => { if (!cancelled) { try { hls.startLoad(); } catch { /* noop */ } } }, 1200 * retry);
        return;
      }
      try { hls.destroy(); } catch { /* noop */ }
      hlsRef.current = null;
      fallback();
    });

    hls.loadSource(cam.url);
    hls.attachMedia(v);
    // Jaring pengaman: bila manifest tak pernah terbaca dan tidak ada galat fatal,
    // paksa cadangan supaya sel tidak diam hitam selamanya.
    watchdog = setTimeout(fallback, 15000);

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      v.removeEventListener('playing', onPlaying);
      try { hls.destroy(); } catch { /* noop */ }
      hlsRef.current = null;
    };
  }, [cam.url, cam.clip, cam.live, link, index]);

  // Sel di luar layar dijeda dan unduhan segmen dihentikan.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (visible) {
      hlsRef.current?.startLoad();
      v.play().catch(() => {});
    } else {
      v.pause();
      hlsRef.current?.stopLoad();
    }
  }, [visible, link]);

  const status = link === 'linking'
    ? { label: lang === 'id' ? 'MENYAMBUNG' : 'LINKING', color: '#ffd60a' }
    : OK;

  return (
    <div ref={boxRef} className="relative w-full h-full overflow-hidden rounded-lg"
      data-cctv-state={link}
      style={{ background: '#000', border: '1px solid var(--border-dim)' }}>
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        disablePictureInPicture
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: 'cover' }}
      />

      {/* Bar atas: nomor kamera, lokasi */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-start gap-2 px-2 py-1.5 font-mono"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.72), transparent)' }}>
        <span className="text-[10px] font-black tracking-wider px-1.5 rounded"
          style={{ background: 'rgba(0,212,255,0.18)', color: '#8ff0ff', border: '1px solid rgba(0,212,255,0.35)' }}>
          {cam.id}
        </span>
        <span className="text-[10px] font-bold truncate" style={{ color: '#eaffff', textShadow: '0 1px 2px #000' }}>
          {cam.nama}
        </span>
      </div>

      {/* Bar bawah: REC berkedip, lokasi, sumber, status */}
      <div className="absolute bottom-0 inset-x-0 z-10 flex items-end gap-2 px-2 py-1.5 font-mono"
        style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.75), transparent)' }}>
        <span className="flex items-center gap-1 text-[9px] font-black" style={{ color: '#ff2d55', textShadow: '0 1px 2px #000' }}>
          <span className="cctv-rec inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#ff2d55' }} />
          REC
        </span>
        <span className="text-[9px] font-bold truncate" style={{ color: 'rgba(234,255,255,0.75)' }}>
          {cam.lokasi}
        </span>
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          <span className="text-[8px] uppercase tracking-wider" style={{ color: 'rgba(234,255,255,0.55)' }}>
            {cam.host}
          </span>
          <span className="flex items-center gap-1 text-[9px] font-black px-1.5 rounded"
            style={{ background: 'rgba(0,0,0,0.55)', color: status.color, border: `1px solid ${status.color}55` }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: status.color }} />
            {status.label}
          </span>
        </span>
      </div>
    </div>
  );
};

const CctvWall = () => {
  const { lang } = useApp();
  const [cols, setCols] = useState(loadCols);
  const [zoom, setZoom] = useState(null);
  const [now, setNow] = useState(() => new Date());
  // Mode sunting anotasi di tampilan perbesar: false | 'roi' | 'garis'.
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setEditMode(false); setZoom(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  // Keluar dari perbesar selalu menutup mode sunting.
  useEffect(() => { if (!zoom) setEditMode(false); }, [zoom]);

  const clock = formatClock(now);
  const tiles = buildTiles(cols * cols);
  const liveShown = Math.min(MAX_LIVE, LIVE_FEEDS.length, cols * cols);

  const pickCols = useCallback((c) => {
    setCols(c);
    try { sessionStorage.setItem(SESSION_KEY_COLS, String(c)); } catch { /* noop */ }
  }, []);

  return (
    <div className="flex-1 flex flex-col gap-2 overflow-hidden" style={{ minHeight: 0 }}>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <Camera size={12} color="var(--color-cyan)" />
        <span className="text-[9px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
          {lang === 'id' ? 'DINDING MONITOR CCTV' : 'CCTV MONITOR WALL'}
        </span>
        <span className="text-[9px] font-mono px-1.5 rounded"
          style={{ background: 'rgba(0,255,136,0.10)', border: '1px solid rgba(0,255,136,0.25)', color: 'var(--color-green)' }}>
          {lang === 'id' ? `${liveShown} live · maks ${MAX_LIVE}` : `${liveShown} live · max ${MAX_LIVE}`}
        </span>
        <span className="text-[9px] font-mono tabular-nums" style={{ color: 'var(--text-mute)' }}>
          {clock.jam} WIB
        </span>
        <div className="flex-1" />
        <span className="text-[8px] font-mono uppercase tracking-widest" style={{ color: 'var(--text-mute)' }}>
          {lang === 'id' ? 'Tata letak' : 'Layout'}
        </span>
        <div className="flex gap-1">
          {OPTIONS.map(c => (
            <button key={c} onClick={() => pickCols(c)}
              className="px-2.5 py-1 rounded-lg text-[8px] font-black transition-all"
              style={{
                background: cols === c ? 'rgba(0,212,255,0.15)' : 'var(--color-card)',
                border: `1px solid ${cols === c ? 'rgba(0,212,255,0.4)' : 'var(--border-dim)'}`,
                color: cols === c ? 'var(--color-cyan)' : 'var(--text-mute)',
              }}>
              {c}×{c}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto c-scroll" style={{ minHeight: 0 }}>
        <div className="grid gap-2 pb-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {tiles.map((cam, i) => {
            const key = `${cam.id}-${i}`;
            return (
              <div key={key} data-cctv-key={key} className="relative cursor-pointer group"
                style={{ aspectRatio: '16 / 9', minHeight: cols >= 4 ? 120 : 0 }}
                onClick={() => setZoom(cam)}>
                <CctvCell cam={cam} index={i} />
                {/* Anotasi ROI + garis hitung per kamera (tampil saja). */}
                <CctvAnotasi camId={cam.id} editing={false} />
                {/* Jam overlay per sel, sinkron satu interval */}
                <div className="absolute z-10 font-mono text-[10px] font-black tabular-nums pointer-events-none"
                  style={{ top: 7, right: 8, color: '#eaffff', textShadow: '0 1px 2px #000' }}>
                  {clock.jam}
                  <span className="block text-[8px] font-normal text-right" style={{ color: 'rgba(234,255,255,0.6)' }}>
                    {clock.tanggal}
                  </span>
                </div>
                <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{ background: 'rgba(0,0,0,0.25)' }}>
                  <Maximize2 size={16} color="#8ff0ff" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Portal: lepas dari induk yang memakai transform/filter, supaya overlay
          benar-benar menutup seluruh layar (fixed di dalam induk bertransform
          hanya menutup kotak induknya). */}
      {zoom && createPortal((
        <div className="fixed inset-0 z-[60] flex flex-col p-4 gap-2"
          style={{ background: '#02060e' }}>
          <div className="flex items-center gap-2 shrink-0 font-mono">
            <span className="text-[10px] font-black px-1.5 rounded"
              style={{ background: 'rgba(0,212,255,0.18)', color: '#8ff0ff', border: '1px solid rgba(0,212,255,0.35)' }}>
              {zoom.id}
            </span>
            <span className="text-xs font-bold" style={{ color: 'var(--text-hi)' }}>{zoom.nama}</span>
            <span className="text-[9px]" style={{ color: 'var(--text-mute)' }}>{zoom.lokasi}</span>
            <span className="ml-auto text-[10px] tabular-nums" style={{ color: 'var(--text-dim)' }}>
              {clock.jam} WIB · {clock.tanggal}
            </span>
            {/* Alat operator: gambar ROI dan garis hitung per arah. */}
            <div className="flex items-center gap-1 ml-2">
              <button onClick={() => setEditMode(editMode === 'roi' ? false : 'roi')}
                className="text-[9px] font-black px-2 py-1 rounded"
                style={{
                  background: editMode === 'roi' ? 'rgba(0,212,255,0.2)' : 'var(--color-card)',
                  border: '1px solid rgba(0,212,255,0.4)', color: 'var(--color-cyan)',
                }}>
                {lang === 'id' ? 'GAMBAR ROI' : 'DRAW ROI'}
              </button>
              <button onClick={() => setEditMode(editMode === 'garis' ? false : 'garis')}
                className="text-[9px] font-black px-2 py-1 rounded"
                style={{
                  background: editMode === 'garis' ? 'rgba(0,255,136,0.2)' : 'var(--color-card)',
                  border: '1px solid rgba(0,255,136,0.4)', color: 'var(--color-green)',
                }}>
                {lang === 'id' ? 'GAMBAR GARIS' : 'DRAW LINE'}
              </button>
            </div>
            <button onClick={() => { setEditMode(false); setZoom(null); }} className="p-1 rounded hover:opacity-70 ml-1" aria-label="Tutup">
              <X size={14} color="var(--text-dim)" />
            </button>
          </div>
          <div className="flex-1 min-h-0 mx-auto w-full relative" style={{ maxWidth: 'calc((100vh - 96px) * 16 / 9)' }}>
            <CctvCell cam={zoom} index={0} />
            <CctvAnotasi camId={zoom.id} editing={editMode} onSelesai={() => setEditMode(false)} />
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default CctvWall;
